import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { StatusPoint } from '../../../../shared/interfaces/analytics.interface';
import { formatNumber, formatPercent, statusMeta } from '../../utils/report-format';

interface Segment {
  code: string;
  label: string;
  color: string;
  value: number;
  share: number;
}

/** Barra apilada al 100 % + leyenda con conteo y porcentaje: cómo terminaron las citas. */
@Component({
  selector: 'app-status-breakdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (total === 0) {
      <p class="py-8 text-center text-sm text-gray-500 dark:text-gray-400">No hay citas en estas fechas.</p>
    } @else {
      <div class="flex h-4 gap-0.5 overflow-hidden rounded-md" role="img" [attr.aria-label]="ariaSummary">
        @for (s of segments; track s.code) {
          <div class="h-full first:rounded-l-md last:rounded-r-md" [style.width.%]="s.share" [style.background-color]="s.color" [title]="s.label + ': ' + s.value"></div>
        }
      </div>
      <ul class="mt-5 divide-y divide-gray-100 dark:divide-gray-800">
        @for (s of segments; track s.code) {
          <li class="flex items-center gap-3 py-2.5 text-sm">
            <span class="h-2.5 w-2.5 shrink-0 rounded-sm" [style.background-color]="s.color" aria-hidden="true"></span>
            <span class="flex-1 text-gray-700 dark:text-gray-300">{{ s.label }}</span>
            <span class="font-semibold text-gray-900 tabular-nums dark:text-white">{{ fmtNumber(s.value) }}</span>
            <span class="w-14 text-right text-gray-500 tabular-nums dark:text-gray-400">{{ fmtPercent(s.share) }}</span>
          </li>
        }
      </ul>
      @if (attendanceRate !== null) {
        <p class="mt-4 rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:bg-white/[0.04] dark:text-gray-300">
          <span class="font-semibold text-gray-900 tabular-nums dark:text-white">{{ fmtPercent(attendanceRate) }}</span>
          de asistencia en las citas que ya pasaron (completadas frente a canceladas y no asistió).
        </p>
      }
    }
  `,
})
export class StatusBreakdown {
  @Input() attendanceRate: number | null = null;
  @Input() set data(value: StatusPoint[]) {
    this.total = value.reduce((sum, d) => sum + d.value, 0);
    this.segments = value
      .map((d) => {
        const meta = statusMeta(d.code, d.label);
        return { code: d.code, label: meta.label, color: meta.color, value: d.value, share: this.total ? (d.value / this.total) * 100 : 0, order: meta.order };
      })
      .sort((a, b) => a.order - b.order);
    this.ariaSummary = this.segments.map((s) => `${s.label}: ${s.value}`).join(', ');
  }

  segments: Segment[] = [];
  total = 0;
  ariaSummary = '';

  readonly fmtNumber = formatNumber;
  fmtPercent(v: number): string {
    return formatPercent(v, v < 10 ? 1 : 0);
  }
}
